import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { History, Location } from 'history'
import { generatePath } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Pagination, Table, Tag, Space, Popover, Select, Typography, Button } from 'antd'
import styled from 'styled-components/macro'
import { Col } from 'styled-bootstrap-grid'

import { getProfiles, getTotalProfiles, searchParams } from '../../features/profile/selectors'
import { ProfileActions } from '../../features/profile/actions'
import { Profile } from '../../features/profile/types'
import { Page } from '../../components/Page'
import { Layout } from '../../components/Page/Layout'
import { Title } from '../../components/Text'
import { Row } from '../../components/Grid'
import { EDIT_PROFILE, ADD_PROFILE } from '../../routes'
import { defaultSearchParams } from '../../features/profile/reducer'

const { Option } = Select
const { Text } = Typography
const { Column } = Table

const StyledPagination = styled(Pagination)`
  margin: 20px 0;
  text-align: center;
`

const ResetButton = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: 28px;
  margin-left: 40px;
`

const HeaderCol = styled(Col)`
  .select-appearance,
  .select-hair {
    width: 120px;
  }
  .select-skin,
  .select-breast {
    width: 140px;
  }
`

const ImageThumb = styled.img`
  cursor: pointer;
  width: 48px;
`

type ProfilesProps = {
  history: History
  location: Location<{ showInDashbo9ard?: boolean } | undefined>
}

export const Profiles: React.FC<ProfilesProps> = ({ history }: ProfilesProps) => {
  const dispatch = useDispatch()
  const profiles = useSelector(getProfiles)
  const total = useSelector(getTotalProfiles)

  const searchParameters = useSelector(searchParams)

  const [data, setData] = useState<{ key: number; age: number; photo: string; bio: string; tags: string[] }[]>([])

  useEffect(() => {
    const newData = _.map(profiles, (profile: Profile) => {
      return {
        id: profile.id,
        key: profile.id,
        photo: profile.photos[0].id,
        name: profile.name,
        age: profile.age,
        bio: '',
        tags: [
          profile.appearance,
          profile.hair ?? '- no hair -',
          profile.breast ?? '- no breast -',
          profile.skin ?? ' - no skin -',
          profile.size ?? ' - no size -'
        ]
      }
    })

    setData(newData)
  }, [profiles])

  useEffect(() => {
    const { appearance, age, hair, breast, skin, size, offset, pageSize } = searchParameters
    dispatch(
      ProfileActions.fetchAllProfiles(pageSize, offset, 'CaldeTentazioni', appearance, age, hair, breast, skin, size)
    )
  }, [dispatch, searchParameters])

  const onPageChange = (page: number, sz: number | undefined) => {
    if (page && sz) {
      if (sz !== searchParameters.pageSize) {
        dispatch(
          ProfileActions.setSearch({
            ...searchParameters,
            offset: 0,
            currentPage: 1,
            pageSize: sz
          })
        )
      } else {
        dispatch(
          ProfileActions.setSearch({
            ...searchParameters,
            offset: (page - 1) * searchParameters.pageSize,
            currentPage: page
          })
        )
      }
    }
  }

  const handeChangeParameters = (field: string, value: string) => {
    dispatch(
      ProfileActions.setSearch({
        ...searchParameters,
        [field]: value
      })
    )
  }

  const editProfile = (p: Profile) => {
    history.push(generatePath(EDIT_PROFILE, { profileId: p.id }))
  }

  const handleCreateNewProfile = () => {
    dispatch(ProfileActions.updateSelectedProfile(null))
    history.push(ADD_PROFILE)
  }

  const { appearance, age, hair, breast, skin, size, pageSize, currentPage } = searchParameters

  return (
    <Page>
      <Layout>
        <Title size="XL">Profiles</Title>
        <Row>
          <HeaderCol md={12}>
            <Space direction="horizontal">
              <Space direction="vertical">
                <Text strong>Appearance</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('appearance', value)}
                  className="select-appearance"
                  value={appearance}
                >
                  <Option value="">Tutte</Option>
                  <Option value="ugly">Ugly</Option>
                  <Option value="top_model">Top model</Option>
                  <Option value="normal">Normal</Option>
                </Select>
              </Space>
              <Space direction="vertical">
                <Text strong>Età</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('age', value)}
                  className="select-age"
                  value={age}
                >
                  <Option value="">Tutte</Option>
                  <Option value="18-25">18-25</Option>
                  <Option value="25-40">25-40</Option>
                  <Option value="40-60">40-60</Option>
                  <Option value="60-99">60+</Option>
                </Select>
              </Space>
              <Space direction="vertical">
                <Text strong>Capelli</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('hair', value)}
                  className="select-hair"
                  value={hair}
                >
                  <Option value="">Tutti</Option>
                  <Option value="black">Neri</Option>
                  <Option value="blonde">Biondi</Option>
                  <Option value="brown">Castani</Option>
                  <Option value="red">Rossi</Option>
                </Select>
              </Space>
              <Space direction="vertical">
                <Text strong>Seno</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('breast', value)}
                  className="select-breast"
                  value={breast}
                >
                  <Option value="">Tutti</Option>
                  <Option value="big_breast">Grande</Option>
                  <Option value="small_breast">Piccolo</Option>
                  <Option value="normal_breast">Normale</Option>
                </Select>
              </Space>
              <Space direction="vertical">
                <Text strong>Pelle</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('skin', value)}
                  className="select-skin"
                  value={skin}
                >
                  <Option value="">Tutte</Option>
                  <Option value="white_skin">Bianca</Option>
                  <Option value="black_skin">Nera</Option>
                  <Option value="yellow_skin">Gialla</Option>
                </Select>
              </Space>
              <Space direction="vertical">
                <Text strong>Size</Text>
                <Select
                  defaultValue=""
                  onChange={value => handeChangeParameters('size', value)}
                  className="select-size"
                  value={size}
                >
                  <Option value="">Tutte</Option>
                  <Option value="petite">Piccola</Option>
                  <Option value="thin">Magra</Option>
                  <Option value="fat">Grassa</Option>
                </Select>
              </Space>
              <ResetButton>
                <Button type="default" danger onClick={() => dispatch(ProfileActions.setSearch(defaultSearchParams))}>
                  Reset
                </Button>
              </ResetButton>
            </Space>
          </HeaderCol>
        </Row>
        <Row justifyContent="between" alignItems="center">
          <Col md={6} alignSelf="center">
            <Button type="primary" onClick={handleCreateNewProfile}>
              CREA NUOVO
            </Button>
          </Col>
          <Col md={6} alignSelf="center">
            <StyledPagination
              pageSize={pageSize}
              total={total}
              onChange={onPageChange}
              // onShowSizeChange={onchangePageSize}
              current={currentPage}
            />
          </Col>
        </Row>
        <Row>
          {/* {profiles && _.map(profiles, (profile: Profile) => <ProfileItem key={profile.id} profile={profile} />)} */}
          <Col md={12} alignSelf="center">
            <Table dataSource={data} pagination={false}>
              <Column title="Nome" dataIndex="name" key="name" />
              <Column
                title="Foto"
                dataIndex="photo"
                key="photo"
                render={(url: string, record: any) => (
                  <Popover
                    placement="right"
                    title={record.name}
                    content={
                      <img src={`http://site-collaudo.caldetentazioni.com/images/profiles/${url}.jpg`} alt={url} />
                    }
                    trigger="hover"
                  >
                    <ImageThumb src={`http://site-collaudo.caldetentazioni.com/images/profiles/${url}.jpg`} alt={url} />
                  </Popover>
                )}
              />
              <Column title="Età" dataIndex="age" key="age" />
              <Column
                title="Tags"
                dataIndex="tags"
                key="tags"
                render={tags => (
                  <>
                    {_.map(tags, (tag: string) => {
                      let color = 'default'

                      switch (tag) {
                        case 'big_breast':
                        case 'small_breast':
                        case 'normal_breast':
                          color = 'purple'
                          break
                        case 'white_skin':
                        case 'yellow_skin':
                        case 'black_skin':
                          color = 'pink'
                          break
                        case 'brown':
                        case 'black':
                        case 'blonde':
                        case 'red':
                          color = 'volcano'
                          break
                        case 'petite':
                        case 'fat':
                        case 'thin':
                          color = 'cyan'
                          break
                        case 'top_model':
                        case 'ugly':
                        case 'normal':
                          color = 'gold'
                          break
                        default:
                          break
                      }

                      return (
                        <Tag color={color} key={tag}>
                          {tag}
                        </Tag>
                      )
                    })}
                  </>
                )}
              />
              <Column
                title="Action"
                key="action"
                render={(text: string, record: any) => (
                  <Space size="middle">
                    <Button type="link" onClick={() => editProfile(record)}>
                      Edit
                    </Button>
                  </Space>
                )}
              />
            </Table>
          </Col>
        </Row>
        <Row justifyContent="end">
          <Col md={6} alignSelf="end">
            <StyledPagination
              pageSize={pageSize}
              total={total}
              onChange={onPageChange}
              // onShowSizeChange={onchangePageSize}
              current={currentPage}
            />
          </Col>
        </Row>
      </Layout>
    </Page>
  )
}
